import React, { useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCheck,
  faSpinner,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';

const SearchClientsModal = ({
  show,
  ticketId,
  searchTerm,
  onSearchTermChange,
  clients,
  isLoading,
  page,
  totalPages,
  totalRecords,
  onPrevPage,
  onNextPage,
  onGoToPage,
  onSelectClient,
  isAssigning,
  selectedClientId,
  onClose
}) => {
  const { config: { isDark: contextIsDark } = {} } = useContext(AppContext);
  const isDark = (typeof document !== 'undefined' && (
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark')
  )) || Boolean(contextIsDark);

  if (!show) return null;

  // Helper para generar los números de página visibles
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const total = totalPages || 1;

    if (total <= maxVisible + 2) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, page - 1);
      let end = Math.min(total - 1, page + 1);

      if (page <= 3) {
        start = 2;
        end = 4;
      } else if (page >= total - 2) {
        start = total - 3;
        end = total - 1;
      }

      if (start > 2) pages.push('ellipsis-start');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push('ellipsis-end');

      pages.push(total);
    }
    return pages;
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.65)' : 'rgba(0, 0, 0, 0.35)',
        zIndex: 1060
      }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={e => e.stopPropagation()}
      >
        <div
          className="modal-content shadow-lg"
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: isDark ? '#121e2d' : '#ffffff',
            border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
            color: isDark ? '#f9fafd' : '#0f172a'
          }}
        >
          {/* Header */}
          <div
            className="modal-header py-3 px-4"
            style={{
              backgroundColor: isDark ? '#182635' : '#ffffff',
              borderBottom: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
            }}
          >
            <div>
              <h6
                className="modal-title fw-bold mb-0 d-flex align-items-center"
                style={{ fontSize: '1.05rem', color: isDark ? '#ffffff' : '#0f172a' }}
              >
                <FontAwesomeIcon icon={faSearch} className="text-primary me-2" style={{ fontSize: '0.95rem' }} />
                <span>Búsqueda de Clientes</span>
              </h6>
              <small style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                Ticket #{ticketId} — Asignar cliente por teléfono, nombre o licencia
              </small>
            </div>
            <button
              type="button"
              className={`btn-close ${isDark ? 'btn-close-white' : ''}`}
              onClick={onClose}
            />
          </div>

          {/* Body */}
          <div className="modal-body p-3 p-md-4">
            {/* Input Buscador */}
            <div className="mb-3">
              <div className="input-group">
                <span
                  className="input-group-text border-end-0 ps-3"
                  style={{
                    backgroundColor: isDark ? '#182635' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#d8e2ef',
                    color: isDark ? '#94a3b8' : '#64748b'
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-2"
                  style={{
                    fontSize: '0.90rem',
                    height: '42px',
                    backgroundColor: isDark ? '#182635' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#d8e2ef',
                    color: isDark ? '#f9fafd' : '#0f172a'
                  }}
                  placeholder="Escribe número de teléfono, nombre o licencia del cliente..."
                  value={searchTerm}
                  onChange={e => onSearchTermChange(e.target.value)}
                  autoFocus
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary border-start-0 px-3"
                    style={{ borderColor: isDark ? '#233142' : '#d8e2ef' }}
                    type="button"
                    onClick={() => onSearchTermChange('')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>

            {/* Lista Paginada (Muestra #ID - Nombre) */}
            <div
              className="rounded-3 p-2 border"
              style={{
                minHeight: '260px',
                backgroundColor: isDark ? '#182635' : '#ffffff',
                borderColor: isDark ? '#233142' : '#e2e8f0'
              }}
            >
              {isLoading ? (
                <div className="text-center py-5 text-muted">
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary fs-4" />
                  <div className="mt-2" style={{ fontSize: '0.85rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Buscando clientes...
                  </div>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <div className="fw-semibold mb-1" style={{ fontSize: '0.90rem', color: isDark ? '#cbd5e1' : '#334155' }}>
                    No se encontraron clientes
                  </div>
                  <small style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Prueba buscando con otro término.</small>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {clients.map(c => {
                    const isSelected = selectedClientId === c.idClienteAkasia;
                    return (
                      <div
                        key={c.idClienteAkasia}
                        className="p-3 rounded-2 border d-flex justify-content-between align-items-center"
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          backgroundColor: isSelected
                            ? (isDark ? 'rgba(44, 123, 229, 0.2)' : '#e7f1ff')
                            : (isDark ? '#121e2d' : '#ffffff'),
                          borderColor: isSelected
                            ? '#2c7be5'
                            : (isDark ? '#233142' : '#e2e8f0')
                        }}
                        onMouseEnter={e => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = isDark ? '#182635' : '#f8fafc';
                        }}
                        onMouseLeave={e => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = isDark ? '#121e2d' : '#ffffff';
                        }}
                        onClick={() => onSelectClient(c)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                            style={{
                              width: '36px',
                              height: '36px',
                              backgroundColor: isSelected
                                ? (isDark ? '#1e3a5f' : '#dbeafe')
                                : (isDark ? '#1e293b' : '#f1f5f9'),
                              color: isDark ? '#f9fafd' : '#0f172a',
                              fontSize: '0.80rem'
                            }}
                          >
                            {c.vNombreCompleto ? c.vNombreCompleto.substring(0, 2).toUpperCase() : 'CL'}
                          </div>
                          <div>
                            <div className="fw-bold d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '0.90rem', color: isDark ? '#ffffff' : '#0f172a' }}>
                              <span>#{c.idClienteAkasia} — {c.vNombreCompleto || 'Sin Nombre'}</span>
                              {c.idLicencia && (
                                <span
                                  className="badge"
                                  style={{
                                    fontSize: '0.70rem',
                                    fontWeight: 600,
                                    backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                                    color: isDark ? '#38bdf8' : '#0284c7',
                                    border: isDark ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid #bae6fd',
                                    borderRadius: '4px',
                                    padding: '2px 6px'
                                  }}
                                  title={`Licencia #${c.idLicencia}`}
                                >
                                  Licencia #{c.idLicencia}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <a
                            href={`/clientes/detalle/${c.idClienteAkasia}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary rounded-pill px-2 py-1 shadow-none d-inline-flex align-items-center"
                            style={{ fontSize: '0.78rem' }}
                            title={`Abrir cliente #${c.idClienteAkasia} en otra pestaña`}
                            onClick={e => e.stopPropagation()}
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" style={{ fontSize: '0.70rem' }} />
                            Ver cliente
                          </a>

                          {isSelected ? (
                            <span className="badge bg-primary px-3 py-2" style={{ fontSize: '0.75rem' }}>
                              <FontAwesomeIcon icon={faCheck} className="me-1" /> Asignado
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary rounded-pill px-3 shadow-none"
                              disabled={isAssigning}
                              onClick={e => {
                                e.stopPropagation();
                                onSelectClient(c);
                              }}
                            >
                              {isAssigning ? (
                                <>
                                  <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                  Asignando...
                                </>
                              ) : (
                                'Seleccionar'
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Footer con Selector de Páginas Interactivo */}
          <div
            className="modal-footer py-2 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2"
            style={{
              backgroundColor: isDark ? '#182635' : '#f8fafc',
              borderTop: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
            }}
          >
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Botón Anterior */}
              <button
                type="button"
                className={`btn btn-sm ${isDark ? 'btn-outline-secondary text-light' : 'btn-outline-secondary'} px-2 py-1 shadow-none`}
                style={{ fontSize: '0.80rem', borderRadius: '6px' }}
                onClick={onPrevPage}
                disabled={page <= 1 || isLoading}
              >
                <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                Anterior
              </button>

              {/* Números de página interactivos */}
              <div className="d-flex align-items-center gap-1">
                {getPageNumbers().map((p, idx) => {
                  if (typeof p === 'string') {
                    return (
                      <span key={p + idx} className="px-1" style={{ fontSize: '0.80rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                        ...
                      </span>
                    );
                  }
                  const isCurrent = p === page;
                  return (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm px-2 py-1 shadow-none fw-bold ${
                        isCurrent
                          ? 'btn-primary text-white'
                          : isDark
                            ? 'border-0 bg-transparent text-light hover-bg-dark'
                            : 'border-0 bg-transparent text-dark hover-bg-200'
                      }`}
                      style={{
                        minWidth: '32px',
                        height: '30px',
                        fontSize: '0.80rem',
                        borderRadius: '6px'
                      }}
                      onClick={() => onGoToPage(p)}
                      disabled={isLoading}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>

              {/* Botón Siguiente */}
              <button
                type="button"
                className={`btn btn-sm ${isDark ? 'btn-outline-secondary text-light' : 'btn-outline-secondary'} px-2 py-1 shadow-none`}
                style={{ fontSize: '0.80rem', borderRadius: '6px' }}
                onClick={onNextPage}
                disabled={page >= totalPages || isLoading}
              >
                Siguiente
                <FontAwesomeIcon icon={faChevronRight} className="ms-1" />
              </button>

              {totalRecords > 0 && (
                <span className="ms-2" style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                  ({totalRecords} clientes)
                </span>
              )}
            </div>

            <button
              type="button"
              className="btn btn-sm px-3 shadow-none fw-semibold"
              style={{
                fontSize: '0.82rem',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#38bdf8' : '#0284c7',
                border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                transition: 'all 0.15s ease'
              }}
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchClientsModal;
